import { Group } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type {
	SecodaAiProposalMetadataOut,
	SecodaAIProposalOut,
} from '@repo/api-codegen';
import {
	useApiApproveProposal,
	useApiBulkUpdateResourceRelations,
	useApiRejectProposal,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { EntityType } from '@repo/common/enums/entityType';
import { Button } from '@repo/foundations';
import { Badge } from '@repo/foundations/components/Badge';
import { Text } from '@repo/foundations/components/Text';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
	createSecodaEntity,
	queryClient,
	updateSecodaEntity,
	useCreateDocument,
	useCreateMonitor,
	useCurrentUser,
} from '../../../../api';
import { secodaEntitiesQueryKeyFactory } from '../../../../api/hooks/secodaEntity/constants';
import { fetchSecodaEntity } from '../../../../api/hooks/secodaEntity/fetchSecodaEntities';
import type { ScheduleCadence } from '../../../../api/types/models/schedule';
import { getFieldToUpdate } from '../../../../features/Inbox/utils/notifications';
import { buildResourceUrl } from '../../../../utils/navigationUtils';
import { ANALYTICS_EVENTS } from '../../constants';

const getToastContent = (
	proposal: SecodaAIProposalOut,
	metadata: SecodaAiProposalMetadataOut
): React.ReactNode => {
	switch (proposal.data.proposal_type) {
		case 'glossary_term_creation':
			return (
				<Group position="apart">
					<Text size="sm" color="text/secondary/default">
						Glossary term created
					</Text>
					<Button
						component={Link}
						variant="plain"
						to={`/glossary/${metadata.created_glossary_id}`}
					>
						Go to term
					</Button>
				</Group>
			);
		case 'monitor_creation':
			return (
				<Group position="apart">
					<Text size="sm" color="text/secondary/default">
						Monitor created
					</Text>
					<Button
						component={Link}
						variant="plain"
						to={`/monitor/${metadata.created_monitor_id}`}
					>
						Go to monitor
					</Button>
				</Group>
			);
		case 'document_creation':
			return (
				<Group position="apart">
					<Text size="sm" color="text/secondary/default">
						Document created
					</Text>
					<Button
						component={Link}
						variant="plain"
						to={`/docs/${metadata.created_document_id}`}
					>
						Go to document
					</Button>
				</Group>
			);
		case 'description_suggestion':
			return (
				<Group position="apart">
					<Text size="sm" color="text/secondary/default">
						Description updated
					</Text>
					<Button
						variant="plain"
						component={Link}
						to={buildResourceUrl({
							id: proposal.data.data.entity_id,
							entity_type: metadata.entity_type,
						})}
					>
						View resource
					</Button>
				</Group>
			);

		default:
			return null;
	}
};

const useProposalActionHook = ({
	proposal,
}: {
	proposal: SecodaAIProposalOut;
}) => {
	const [isExecutingProposalAction, setIsExecutingProposalAction] =
		useState(false);

	const { mutateAsync: createMonitor } = useCreateMonitor({});
	const { mutateAsync: createDocument } = useCreateDocument({});
	const { data: currentUser } = useCurrentUser();
	const { mutateAsync: updateBulkResourceRelations } =
		useApiBulkUpdateResourceRelations({});

	const executeProposalAction = async (): Promise<
		SecodaAiProposalMetadataOut | undefined
	> => {
		setIsExecutingProposalAction(true);

		try {
			if (proposal.data.proposal_type === 'glossary_term_creation') {
				const { data } = proposal.data;

				const createdGlossary = await createSecodaEntity({
					data: {
						entity_type: EntityType.glossary,
						title: data.term,
						description: data.description,
						icon: `emoji:${data.emoji}`,
						definition: data.full_definition || '',
						owners: data.owner_id ? [data.owner_id] : undefined,
						teams: data.team_id ? [data.team_id] : undefined,
					},
				});

				if (data.related_entity_ids && data.related_entity_ids.length > 0) {
					await updateBulkResourceRelations({
						body: {
							from_entity: createdGlossary.id,
							to_entities: data.related_entity_ids,
						},
					});
				}

				return {
					created_glossary_id: createdGlossary.id,
				};
			}

			if (proposal.data.proposal_type === 'monitor_creation') {
				const { name, description, integration_id, sql_query, schedule } =
					proposal.data.data;

				const createdMonitor = await createMonitor({
					data: {
						name,
						description,
						integration: integration_id,
						metric_type: 'custom_sql',
						metric_config: {
							query_select: sql_query,
						},
						schedule: {
							cadence: schedule.cadence as ScheduleCadence,
							frequency: schedule.frequency,
							day: schedule.day,
							hour: schedule.hour,
						},
						owners: [currentUser!.id],
					},
				});

				return {
					created_monitor_id: createdMonitor.id,
				};
			}

			if (proposal.data.proposal_type === 'document_creation') {
				const { data } = proposal.data;

				const createdDocument = await createDocument({
					data: {
						entity_type: EntityType.document,
						title: data.title,
						definition: data.definition,
						icon: data.icon || undefined,
						published: data.published || false,
						owners: data.owners || [currentUser!.id],
						teams: data.teams || undefined,
						collections: data.collections || undefined,
					},
				});

				return {
					created_document_id: createdDocument.id,
				} as SecodaAiProposalMetadataOut;
			}

			if (proposal.data.proposal_type === 'description_suggestion') {
				const originalEntity = await fetchSecodaEntity(
					proposal.data.data.entity_id
				);

				// Determine which field to use based on entity type configuration
				const fieldToUpdate = getFieldToUpdate(originalEntity.entity_type);

				await updateSecodaEntity({
					data: {
						id: proposal.data.data.entity_id,
						[fieldToUpdate]: proposal.data.data.description,
					},
				});

				// Clear the cache for the entity
				queryClient.invalidateQueries({
					queryKey: secodaEntitiesQueryKeyFactory.byId(
						proposal.data.data.entity_id
					),
				});

				return {
					entity_type: originalEntity.entity_type,
					old_description:
						fieldToUpdate === 'definition'
							? originalEntity.definition
							: originalEntity.description,
				};
			}
		} catch (error) {
			// To make sure this function doesn't eat error
			throw error;
		} finally {
			// But still set the loading state to false at the end regardless of the result
			setIsExecutingProposalAction(false);
		}

		return undefined;
	};

	return {
		executeProposalAction,
		isExecutingProposalAction,
	};
};

interface ProposalActionsProps {
	proposal: SecodaAIProposalOut;
	postApprovalAction?: () => void;
}

export function ProposalActions({
	proposal,
	postApprovalAction,
}: ProposalActionsProps) {
	const { executeProposalAction, isExecutingProposalAction } =
		useProposalActionHook({ proposal });

	const { mutateAsync: approveProposal, isPending: isApproving } =
		useApiApproveProposal({});

	const { mutateAsync: rejectProposal, isPending: isRejecting } =
		useApiRejectProposal({});

	const handleApproveProposal = async () => {
		try {
			trackEvent(ANALYTICS_EVENTS.AI_SUGGESTION_APPROVE, {
				proposal_id: proposal.id,
				proposal_type: proposal.data.proposal_type,
			});

			const metadata = await executeProposalAction();
			await approveProposal({
				pathParams: { proposalId: proposal.id },
				body: {
					metadata,
				},
			});

			if (metadata) {
				showNotification({
					title: 'Success',
					message: getToastContent(proposal, metadata),
					color: 'green',
					autoClose: 3000,
				});
			}

			if (postApprovalAction) {
				postApprovalAction();
			}

			trackEvent(ANALYTICS_EVENTS.AI_SUGGESTION_APPROVE_SUCCESS, {
				proposal_id: proposal.id,
				proposal_type: proposal.data.proposal_type,
			});
		} catch (error) {
			trackEvent(ANALYTICS_EVENTS.AI_SUGGESTION_APPROVE_FAILED, {
				proposal_id: proposal.id,
				proposal_type: proposal.data.proposal_type,
				error: String(error),
			});
		}
	};

	const handleRejectProposal = async () => {
		trackEvent(ANALYTICS_EVENTS.AI_SUGGESTION_REJECT, {
			proposal_id: proposal.id,
			proposal_type: proposal.data.proposal_type,
		});

		await rejectProposal({
			pathParams: { proposalId: proposal.id },
			body: {},
		});

		showNotification({
			title: 'Success',
			message: 'Suggestion discarded',
			color: 'green',
			autoClose: 3000,
		});
	};

	const approveLabel = useMemo(() => {
		if (proposal.data.proposal_type === 'glossary_term_creation') {
			return 'Create term';
		}
		if (proposal.data.proposal_type === 'monitor_creation') {
			return 'Create monitor';
		}
		if (proposal.data.proposal_type === 'document_creation') {
			return 'Create document';
		}
		if (proposal.data.proposal_type === 'description_suggestion') {
			return 'Update description';
		}

		return 'Approve';
	}, [proposal.data.proposal_type]);

	if (proposal.status !== 'pending') {
		return (
			<Group spacing="md" align="center">
				{/* Status Badge */}
				<Badge
					variant={proposal.status === 'approved' ? 'success' : 'critical'}
					size="md"
				>
					{proposal.status === 'approved'
						? proposal.data.proposal_type === 'description_suggestion'
							? 'Updated'
							: 'Created'
						: 'Discarded'}
				</Badge>

				{/* Navigation Buttons */}
				{proposal.metadata?.created_glossary_id && (
					<Button
						component={Link}
						to={`/glossary/${proposal.metadata.created_glossary_id}`}
						variant="tertiary"
						size="sm"
					>
						Go to Glossary
					</Button>
				)}
				{proposal.metadata?.created_monitor_id && (
					<Button
						component={Link}
						to={`/monitor/${proposal.metadata.created_monitor_id}`}
						variant="tertiary"
						size="sm"
					>
						Go to Monitor
					</Button>
				)}
				{proposal.metadata?.created_document_id && (
					<Button
						component={Link}
						to={`/docs/${proposal.metadata.created_document_id}`}
						variant="tertiary"
						size="sm"
					>
						Go to document
					</Button>
				)}
				{proposal.metadata?.entity_type &&
					proposal.data.proposal_type === 'description_suggestion' && (
						<Button
							component={Link}
							to={buildResourceUrl({
								id: proposal.data.data.entity_id,
								entity_type: proposal.metadata.entity_type,
							})}
							variant="tertiary"
							size="sm"
						>
							Go to Resource
						</Button>
					)}
			</Group>
		);
	}

	return (
		<Group spacing="xs">
			<Button
				variant="default"
				onClick={handleRejectProposal}
				loading={isRejecting}
			>
				Discard
			</Button>
			<Button
				variant="primary"
				tone="default"
				onClick={handleApproveProposal}
				loading={isApproving || isExecutingProposalAction}
			>
				{approveLabel}
			</Button>
		</Group>
	);
}
